import { AlertCircle, Check, Loader2 } from 'lucide-react';
import React from 'react';
import { formatUnits } from 'viem';

import { Button } from '@/components/ui/button';
import { CopyableHash } from '@/components/ui/copyable-hash';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import OperationProgress from '@/features/custodial/components/OperationProgress';
// the store module only: the flow barrel carries the sign-out dialog.
import { useFlowStore } from '@/features/flow/store';
import { cn, shortenAddress } from '@/lib/utils';

import { MIGRATION_COPY } from '../copy';
import { useMigrationStore } from '../store';
import { MIGRATION_STAGES, useMigration } from '../useMigration';
import { useMigrationWalletRequirement } from '../useMigrationWalletRequirement';

type RowState = 'pending' | 'active' | 'done';

const ROW_ICON: Record<RowState, React.ReactNode> = {
    pending: <span className="size-2 rounded-full bg-muted-foreground/50" />,
    active: <Loader2 size={16} className="animate-spin text-primary" />,
    done: <Check size={16} className="text-success" />,
};

/**
 * USDC base units (6dp) as USD with two decimals, rounded down and exact (no
 * float on the way): never a cent more than moves.
 */
function formatUsd(raw: bigint): string {
    const [whole, fraction = ''] = formatUnits(raw / 10_000n, 2).split('.');
    return `${whole}.${fraction.padEnd(2, '0')}`;
}

const StepList: React.FC<{ stage: number; step: string | null }> = ({
    stage,
    step,
}) => (
    <ol className="w-full divide-y divide-border rounded-xl border border-border text-left">
        {MIGRATION_STAGES.map((row, index) => {
            const state: RowState =
                index < stage ? 'done' : index === stage ? 'active' : 'pending';
            return (
                <li
                    key={row.step}
                    data-state={state}
                    className="flex flex-col gap-2 p-3"
                >
                    <div className="flex items-center gap-3">
                        <div
                            className={cn(
                                'flex size-8 shrink-0 items-center justify-center rounded-lg border',
                                state === 'done'
                                    ? 'border-success/40 bg-success/10'
                                    : 'border-border bg-secondary/40',
                            )}
                        >
                            {ROW_ICON[state]}
                        </div>
                        <p
                            className={cn(
                                'min-w-0 flex-1 text-sm',
                                state === 'pending'
                                    ? 'text-muted-foreground'
                                    : 'font-semibold text-foreground',
                            )}
                        >
                            {row.label}
                        </p>
                        <span className="shrink-0 rounded-md border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                            {row.chain}
                        </span>
                    </div>
                    {state === 'active' && (
                        <OperationProgress direction="migrate" step={step} />
                    )}
                </li>
            );
        })}
    </ol>
);

/** The dialog's body; mounted only while the dialog is open. */
const MigrationDialogContent: React.FC = () => {
    const close = useMigrationStore((state) => state.close);
    const requestWalletAuthority = useMigrationStore(
        (state) => state.requestWalletAuthority,
    );
    const switchTo = useFlowStore((state) => state.switchTo);
    const migration = useMigration();
    const { status } = migration;
    // nothing may close the dialog while the wallet prompt is open or money
    // is moving: the progress is the only place the user can follow it.
    const busy = status === 'signing' || status === 'running';
    const holdOpen = (event: Event) => {
        if (busy) event.preventDefault();
    };

    const showForm =
        status === 'idle' || status === 'blocked' || status === 'needsWallet';
    // the reserve note goes with an amount read from the quote
    const showReserve = migration.amountRaw !== null && migration.reserveStays;
    const paidTo = migration.paidTo
        ? shortenAddress(migration.paidTo)
        : 'your Snappy Wallet';
    // what moved is the job's own figure; without one, no amount is claimed.
    const doneSentence =
        migration.migratedRaw === null
            ? MIGRATION_COPY.doneNoAmount(paidTo)
            : MIGRATION_COPY.done(formatUsd(migration.migratedRaw), paidTo);

    return (
        <DialogContent
            className="sm:max-w-md"
            showCloseButton={!busy}
            onEscapeKeyDown={holdOpen}
            onPointerDownOutside={holdOpen}
            onInteractOutside={holdOpen}
        >
            <DialogHeader>
                <DialogTitle>{MIGRATION_COPY.title}</DialogTitle>
                <DialogDescription>
                    {MIGRATION_COPY.description}
                </DialogDescription>
            </DialogHeader>

            {showForm && (
                <div className="flex min-w-0 flex-col gap-4">
                    <div className="rounded-xl border border-border p-3.5">
                        <p className="text-xs text-muted-foreground">
                            {MIGRATION_COPY.amountLabel}
                        </p>
                        {migration.amountRaw !== null ? (
                            <p className="mt-0.5 font-mono text-lg font-semibold text-foreground">
                                ${formatUsd(migration.amountRaw)}
                            </p>
                        ) : migration.quoteLoading ? (
                            <Loader2
                                role="status"
                                aria-label={MIGRATION_COPY.amountLoading}
                                className="mt-1.5 size-4 animate-spin text-muted-foreground"
                            />
                        ) : (
                            <p className="mt-1 text-xs text-muted-foreground">
                                {MIGRATION_COPY.amountUnavailable}
                            </p>
                        )}
                        {showReserve && (
                            <p className="mt-1 text-xs text-muted-foreground">
                                {MIGRATION_COPY.reserveNote}
                            </p>
                        )}
                        {migration.amountRaw === 0n && (
                            <p className="mt-1 text-xs text-muted-foreground">
                                {MIGRATION_COPY.nothingToMigrate}
                            </p>
                        )}
                    </div>

                    <div className="flex min-w-0 flex-col gap-1.5">
                        <p className="text-xs text-muted-foreground">
                            {MIGRATION_COPY.destinationLabel}
                        </p>
                        {migration.destination ? (
                            <CopyableHash
                                value={migration.destination}
                                label="Snappy Wallet"
                                lead={6}
                                tail={4}
                                className="self-start text-xs"
                            />
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                {MIGRATION_COPY.noDestination}
                            </p>
                        )}
                    </div>

                    <div>
                        <p className="text-xs font-semibold text-foreground">
                            {MIGRATION_COPY.whatHappensTitle}
                        </p>
                        <ol className="mt-1.5 list-decimal space-y-1 pl-5 text-xs leading-relaxed text-muted-foreground">
                            {MIGRATION_COPY.whatHappens.map((line) => (
                                <li key={line}>{line}</li>
                            ))}
                        </ol>
                    </div>

                    {status === 'blocked' && (
                        <p
                            role="alert"
                            className="flex items-center gap-2 text-sm text-destructive"
                        >
                            <AlertCircle className="size-4 shrink-0" />
                            {MIGRATION_COPY.blocked}
                        </p>
                    )}

                    {status === 'needsWallet' && (
                        <div className="flex flex-col gap-2 rounded-xl border border-border bg-secondary/30 p-3.5">
                            <p className="text-sm text-foreground">
                                {MIGRATION_COPY.needsWallet}
                            </p>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={requestWalletAuthority}
                            >
                                {MIGRATION_COPY.connectWallet}
                            </Button>
                        </div>
                    )}

                    <Button
                        type="button"
                        disabled={!migration.canStart}
                        onClick={() => void migration.start()}
                    >
                        {MIGRATION_COPY.start}
                    </Button>
                </div>
            )}

            {status === 'signing' && (
                <p className="flex items-center gap-2 text-sm text-foreground">
                    <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
                    {MIGRATION_COPY.signing}
                </p>
            )}

            {status === 'running' && (
                <div className="flex flex-col gap-3">
                    <p className="text-sm text-muted-foreground">
                        {MIGRATION_COPY.running}
                    </p>
                    <StepList stage={migration.stage} step={migration.step} />
                </div>
            )}

            {status === 'done' && (
                <div className="flex min-w-0 flex-col gap-3 [overflow-wrap:anywhere]">
                    <StepList stage={migration.stage} step={null} />
                    <p className="text-sm font-semibold text-foreground">
                        {doneSentence}
                    </p>
                    <p className="text-sm text-muted-foreground">
                        {MIGRATION_COPY.doneNext}
                    </p>
                    {migration.gasMissing && (
                        <p className="text-xs text-muted-foreground">
                            {MIGRATION_COPY.gasMissing}
                        </p>
                    )}
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            className="flex-1"
                            onClick={() => switchTo('v2')}
                        >
                            {MIGRATION_COPY.switchToV2}
                        </Button>
                        <Button type="button" variant="outline" onClick={close}>
                            {MIGRATION_COPY.close}
                        </Button>
                    </div>
                </div>
            )}

            {status === 'failed' && (
                <div className="flex min-w-0 flex-col gap-3 [overflow-wrap:anywhere]">
                    <div>
                        <p
                            role="alert"
                            className="flex items-center gap-2 text-sm font-semibold text-destructive"
                        >
                            <AlertCircle className="size-4 shrink-0" />
                            {MIGRATION_COPY.failedTitle}
                        </p>
                        {migration.error && (
                            <p className="mt-1 text-sm text-foreground">
                                {migration.error}
                            </p>
                        )}
                        <p className="mt-1 text-xs text-muted-foreground">
                            {MIGRATION_COPY.failedSafety}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            className="flex-1"
                            onClick={() => void migration.retry()}
                        >
                            {MIGRATION_COPY.retry}
                        </Button>
                        <Button type="button" variant="outline" onClick={close}>
                            {MIGRATION_COPY.close}
                        </Button>
                    </div>
                </div>
            )}
        </DialogContent>
    );
};

/**
 * The V1 to V2 migration dialog (plan 2026-10-04-v1-to-v2-migration), mounted
 * once in `App.tsx` and opened by the header's Migrate button. Its body (and
 * the migration hook with it) only mounts while open, so a closed dialog reads
 * and polls nothing.
 */
const MigrationDialog: React.FC = () => {
    useMigrationWalletRequirement();
    const isOpen = useMigrationStore((state) => state.isOpen);
    const close = useMigrationStore((state) => state.close);

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) close();
            }}
        >
            {isOpen && <MigrationDialogContent />}
        </Dialog>
    );
};

MigrationDialog.displayName = 'MigrationDialog';

export default MigrationDialog;
